import React, { useEffect, useState } from 'react';
import { AlertHistoryEntry, DeviationReport } from '../../types';
import { sectionStyle, btnStyle, formatDate } from './surveillanceShared';
import { fetchDeviations, submitDeviation } from '../../services/alertsApi';

interface HistoryTabProps {
    history: AlertHistoryEntry[];
    timezone: string | null | undefined;
    onRefresh: () => void;
    /** IEEE-adress att filtrera på, t.ex. från en pushnotis. null = visa alla. */
    focusDevice?: string | null;
    onClearFocus?: () => void;
}

const fmt = (iso: string | null | undefined, tz?: string | null) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('sv-SE', { timeZone: tz || undefined });
    } catch { return iso; }
};

const DeviationBadge: React.FC<{ item: AlertHistoryEntry; tz?: string | null }> = ({ item, tz }) => {
    const [open, setOpen] = useState(false);
    if (!item.deviation_token) return null;
    const submitted = item.deviation_submitted;
    return (
        <div style={{ marginTop: 4 }}>
            <span
                onClick={() => setOpen(o => !o)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer',
                    fontSize: 12, padding: '2px 8px', borderRadius: 4,
                    background: submitted ? '#d1fae5' : '#fef3c7',
                    color: submitted ? '#065f46' : '#92400e',
                    border: `1px solid ${submitted ? '#6ee7b7' : '#fcd34d'}`,
                    userSelect: 'none',
                }}
            >
                {submitted ? '✅ Rapporterad' : '⏳ Ej rapporterad'}
                <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
            </span>
            {open && submitted && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#444', lineHeight: 1.5, background: '#f9fafb', borderRadius: 6, padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                    {item.deviation_reporter_name && <p style={{ margin: '0 0 4px' }}><strong>Rapportör:</strong> {item.deviation_reporter_name}</p>}
                    <p style={{ margin: '0 0 4px' }}><strong>Orsak:</strong> {item.deviation_reason || '—'}</p>
                    <p style={{ margin: '0 0 4px' }}><strong>Åtgärd:</strong> {item.deviation_action || '—'}</p>
                </div>
            )}
        </div>
    );
};

const DeviationRow: React.FC<{ d: DeviationReport; tz?: string | null }> = ({ d, tz }) => {
    const [open, setOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [reporterName, setReporterName] = useState('');
    const [reason, setReason] = useState('');
    const [actionTaken, setActionTaken] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(!!d.submitted_at);
    const [submittedData, setSubmittedData] = useState({ reason: d.reason, action_taken: d.action_taken, reporter_name: d.reporter_name, submitted_at: d.submitted_at });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reason.trim() || !actionTaken.trim()) return;
        setSaving(true); setSaveError(null);
        const result = await submitDeviation(d.token, {
            reporter_name: reporterName.trim() || null,
            reason: reason.trim(),
            action_taken: actionTaken.trim(),
        });
        if (result?.error || !result) {
            setSaveError(result?.error || 'Kunde inte spara. Försök igen.');
        } else {
            setSubmitted(true);
            setSubmittedData({ reason: reason.trim(), action_taken: actionTaken.trim(), reporter_name: reporterName.trim() || null, submitted_at: new Date().toISOString() });
            setFormOpen(false);
        }
        setSaving(false);
    };
    return (
        <>
            <tr
                onClick={() => submitted && setOpen(o => !o)}
                style={{ borderBottom: '1px solid #eee', cursor: submitted ? 'pointer' : 'default', background: open ? '#f9fafb' : undefined }}
            >
                <td style={{ padding: '9px 10px', fontWeight: 500 }}>{d.rule_name}</td>
                <td style={{ padding: '9px 10px', color: '#555' }}>{d.device_name}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>{fmt(d.alert_triggered_at, tz)}</td>
                <td style={{ padding: '9px 10px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>{fmt(d.recovered_at, tz)}</td>
                <td style={{ padding: '9px 10px' }}>
                    {submitted
                        ? <span style={{ background: '#d1fae5', color: '#065f46', fontSize: 12, padding: '2px 8px', borderRadius: 4, border: '1px solid #6ee7b7' }}>✅ Rapporterad</span>
                        : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 12, padding: '2px 8px', borderRadius: 4, border: '1px solid #fcd34d' }}>⏳ Ej rapporterad</span>
                            <button onClick={e => { e.stopPropagation(); setFormOpen(o => !o); setOpen(false); }} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}>Rapportera</button>
                          </span>}
                </td>
                <td style={{ padding: '9px 10px', color: '#555', fontSize: 13 }}>{submittedData.reporter_name || '—'}</td>
            </tr>
            {formOpen && !submitted && (
                <tr style={{ borderBottom: '1px solid #eee', background: '#eff6ff' }}>
                    <td colSpan={6} style={{ padding: '12px 16px' }}>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520 }}>
                            <strong style={{ fontSize: 13, color: '#1d4ed8' }}>Rapportera avvikelse</strong>
                            <input
                                placeholder="Ditt namn (valfritt)"
                                value={reporterName} onChange={e => setReporterName(e.target.value)}
                                style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 13 }}
                            />
                            <textarea
                                placeholder="Orsak till avvikelse *"
                                value={reason} onChange={e => setReason(e.target.value)} required rows={2}
                                style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 13, resize: 'vertical' }}
                            />
                            <textarea
                                placeholder="Åtgärd *"
                                value={actionTaken} onChange={e => setActionTaken(e.target.value)} required rows={2}
                                style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 13, resize: 'vertical' }}
                            />
                            {saveError && <span style={{ color: '#dc2626', fontSize: 12 }}>{saveError}</span>}
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="submit" disabled={saving} style={{ padding: '6px 16px', borderRadius: 4, border: 'none', background: '#2563eb', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>{saving ? 'Sparar…' : 'Skicka in'}</button>
                                <button type="button" onClick={() => setFormOpen(false)} style={{ padding: '6px 16px', borderRadius: 4, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer', fontSize: 13 }}>Avbryt</button>
                            </div>
                        </form>
                    </td>
                </tr>
            )}
            {open && submitted && (
                <tr style={{ borderBottom: '1px solid #eee', background: '#f9fafb' }}>
                    <td colSpan={6} style={{ padding: '10px 16px 12px' }}>
                        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                            {submittedData.submitted_at && <p style={{ margin: '0 0 4px', color: '#666', fontSize: 12 }}>Inskickad: {fmt(submittedData.submitted_at, tz)}</p>}
                            <p style={{ margin: '0 0 4px' }}><strong>Orsak:</strong> {submittedData.reason || '—'}</p>
                            <p style={{ margin: 0 }}><strong>Åtgärd:</strong> {submittedData.action_taken || '—'}</p>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

const HistoryTab: React.FC<HistoryTabProps> = ({ history, timezone, onRefresh, focusDevice, onClearFocus }) => {
    const [tab, setTab] = useState<'history' | 'deviations'>('history');
    const shown = focusDevice ? history.filter(h => h.device_ieee === focusDevice) : history;
    const focusName = focusDevice ? (shown[0]?.device_name ?? focusDevice) : null;
    const [deviations, setDeviations] = useState<DeviationReport[]>([]);
    const [devLoading, setDevLoading] = useState(false);

    useEffect(() => {
        if (tab === 'deviations') {
            setDevLoading(true);
            fetchDeviations()
                .then(d => setDeviations(d || []))
                .finally(() => setDevLoading(false));
        }
    }, [tab]);

    const tabBtn = (id: 'history' | 'deviations', label: string) => (
        <button
            onClick={() => setTab(id)}
            style={{
                padding: '7px 18px', border: 'none', borderRadius: '6px 6px 0 0',
                background: tab === id ? 'white' : '#e5e7eb',
                fontWeight: tab === id ? 700 : 400,
                color: tab === id ? '#1e3a5f' : '#555',
                cursor: 'pointer', fontSize: 14, borderBottom: tab === id ? '2px solid white' : 'none',
            }}
        >{label}</button>
    );

    return (
        <div>
            <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #ddd', marginBottom: 16 }}>
                {tabBtn('history', 'Larmhistorik')}
                {tabBtn('deviations', 'Avvikelserapporter')}
            </div>

            {tab === 'history' && (
                <>
                    <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {focusDevice ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#666' }}>
                                Larm för <strong style={{ color: '#1e3a5f' }}>{focusName}</strong>
                                <button
                                    onClick={onClearFocus}
                                    style={{ fontSize: 12, padding: '2px 10px', borderRadius: 12, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}
                                >
                                    Visa alla
                                </button>
                            </span>
                        ) : (
                            <span style={{ color: '#666' }}>De 50 senaste larmen</span>
                        )}
                        <button onClick={onRefresh} style={btnStyle('#6c757d')}>↺ Uppdatera</button>
                    </div>
                    {shown.length === 0 ? (
                        <div style={{ ...sectionStyle, textAlign: 'center', color: '#666' }}>
                            {focusDevice ? 'Inga larm för den här enheten bland de 50 senaste.' : 'Inga larm har gått än.'}
                        </div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Tid</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Regel</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Enhet</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Villkor</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Värde</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Kontakter</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map(h => (
                                    <tr key={h.id} style={{ borderBottom: '1px solid #eee', background: h.is_recovery ? '#f0fdf4' : undefined }}>
                                        <td style={{ padding: '10px', fontSize: '13px', color: '#555', whiteSpace: 'nowrap' }}>{formatDate(h.triggered_at, timezone ?? undefined)}</td>
                                        <td style={{ padding: '10px', fontWeight: 'bold' }}>
                                            {h.rule_name}
                                            {h.source === 'advanced' && (
                                                <span style={{
                                                    marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#6a1b9a',
                                                    background: '#f3e5f5', border: '1px solid #e1bee7',
                                                    borderRadius: 4, padding: '1px 6px',
                                                }}>
                                                    avancerad
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '10px', color: '#555' }}>{h.device_name}</td>
                                        <td style={{ padding: '10px' }}>
                                            {/* Avancerade regler har ingen jämförelse att visa — deras
                                                `property` är redan en färdig mening. */}
                                            {h.operator == null
                                                ? <span style={{ fontSize: '13px', color: '#444' }}>{h.property}</span>
                                                : <code style={{ fontSize: '13px' }}>{h.property} {h.operator} {h.threshold}</code>}
                                        </td>
                                        <td style={{ padding: '10px' }}>
                                            {h.is_recovery
                                                ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12 }}>✓ Återhämtad</span>
                                                : <span style={{ backgroundColor: '#fff3cd', padding: '2px 8px', borderRadius: '4px', fontWeight: 'bold' }}>{h.value}</span>}
                                            {h.is_recovery && <DeviationBadge item={h} tz={timezone} />}
                                        </td>
                                        <td style={{ padding: '10px', fontSize: '13px', color: '#555' }}>
                                            {(h.contacts_notified || []).map((c: any) => c.name).join(', ') || '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}

            {tab === 'deviations' && (
                <>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#666' }}>Avvikelserapporter för larmhändelser</span>
                        <button onClick={() => {
                            setDevLoading(true);
                            fetchDeviations().then(d => setDeviations(d || [])).finally(() => setDevLoading(false));
                        }} style={btnStyle('#6c757d')}>↺ Uppdatera</button>
                    </div>
                    {devLoading ? (
                        <div style={{ ...sectionStyle, textAlign: 'center', color: '#666' }}>Laddar…</div>
                    ) : deviations.length === 0 ? (
                        <div style={{ ...sectionStyle, textAlign: 'center', color: '#666' }}>Inga avvikelserapporter hittades.</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Regel</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Enhet</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Larmtid</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Återhämtning</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Rapportör</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deviations.map(d => <DeviationRow key={d.id} d={d} tz={timezone} />)}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </div>
    );
};

export default HistoryTab;

